import React, { FC, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';

import { NotificationType } from 'app/api/services/notifications/types';

import { API, useFetch } from 'app/api';
import { SsrModule } from 'app/constants/ssrModules';
import { Spinner } from 'app/design-system/components/Spinner';
import { TitleFontSize, Widget } from 'app/design-system/components/Widget/Widget';
import { useTranslation } from 'app/lang';
import useComponentWillMount from 'app/lib/hooks/useComponentWillMount';
import Footer from 'app/modules/Layout/Footer';
import { useComponentWillReceiveProps } from 'app/packages/lib/hooks';
import ErrorBoundary from 'app/widgets/ErrorBoundary';
import { Notification } from 'app/widgets/Notification';

import { EmptyState } from './EmptyState';

import dictionary from './Notifications.dictionary.json';

import styles from './Notifications.module.scss';

export const Notifications: FC = () => {
  const { t, lang } = useTranslation();
  const getAllNotificationsAPI = useFetch(
    API.notifications.getAllNotifications,
    { ssrUniqKey: 'notifications' },
  );
  const markReadAllNotificationsAPI = useFetch(
    API.notifications.markReadAllNotifications,
    { skipSSR: true },
  );

  const notifications = getAllNotificationsAPI.data?.notifications;

  const getAllNotifications = () => {
    getAllNotificationsAPI.makeRequest({
      size: 999,
      pageNumber: 1,
    });
  };

  useComponentWillMount(() => {
    if (!getAllNotificationsAPI.requested) {
      getAllNotifications();
    }
  });

  useComponentWillReceiveProps(() => {
    getAllNotifications();
  }, [lang]);

  useEffect(() => {
    return () => {
      markReadAllNotificationsAPI.makeRequest();
    };
  }, []);

  const content = useMemo(() => {
    if (!notifications?.length) {
      return <EmptyState />;
    }

    return notifications.map((item) => {
      // TODO: refactor: move this logic to Notification
      // pass to Notification only item
      switch (item.type) {
        case NotificationType.DEPOSIT:
        case NotificationType.WITHDRAWAL:
        case NotificationType.CASHBACK:
          return (
            <Notification key={item.id}
                          className={styles.item}
                          title={item.payload.title}
                          body={item.payload.body}
                          type={item.type}
                          date={new Date(item.date)}
                          balanceChange={item.payload.parameters.sum}
                          currency={item.payload.parameters.currency} />
          );
        default:
          return (
            <Notification key={item.id}
                          className={styles.item}
                          title={item.payload.title}
                          body={item.payload.body}
                          type={item.type}
                          date={new Date(item.date)} />
          );
      }
    });
  }, [getAllNotificationsAPI.loading, notifications]);

  return (
    <>
      <Helmet>
        <title>
          {t(dictionary.title)}
        </title>
      </Helmet>

      <ErrorBoundary>
        {getAllNotificationsAPI.loading || !getAllNotificationsAPI.requested ? (
          <Spinner className={styles.spinner} />
        ) : (
          <section className={styles.container}
                   data-ssr-id={SsrModule.NOTIFICATIONS}>
            <Widget contentClassName={styles.widgetContent}
                    title={t(dictionary.title)}
                    titleFontSize={TitleFontSize.LARGE}>
              {content}
            </Widget>
          </section>
        )}

        <Footer />
      </ErrorBoundary>
    </>
  );
};
